/**
 * <AccountIdentityCard> — qui est l'élève, en tête de « Mon compte » (organism).
 *
 * Son nom, sa classe, ses matières prioritaires, depuis quand il est là, et son avatar. Le
 * crayon sur l'avatar dit qu'il se change : tout l'avatar presse et ouvre son menu (le menu
 * lui-même est `<AccountAvatarMenu>`). Sans `onEditAvatar`, l'avatar est une image.
 *
 * L'XP et les pétales sont les compteurs de son header (`<StudentCounters>`), sous le reste : un
 * compteur se dessine d'une seule façon. Ils n'existent que s'ils sont donnés : retirer la
 * gamification, c'est ne plus les passer.
 *
 * `titleProgress` ajoute, sous un filet, le titre de l'élève et son chemin vers le suivant
 * (`<TitleProgress>`, l'anneau du livre) : le titre fait partie de qui il est, et l'ancien écran
 * le posait déjà sous son avatar. Avec `onChooseTitle`, la ligne ouvre le choix du titre.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UIStudentAccountIdentity } from "../../../ports/primary/ui/types"
import { Avatar } from "../../atoms/Avatar"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { Icon } from "../../atoms/icons/Icon"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { Card } from "../../molecules/Card"
import { StudentCounters } from "../../molecules/StudentCounters"
import { SubjectBadges } from "../../molecules/SubjectBadges"
import { TitleProgress, type TitleProgressProps } from "../../molecules/TitleProgress"

export type { UIStudentAccountIdentity }

/** Le diamètre de l'avatar du compte : la plus grande taille de l'avatar du livre. */
export const ACCOUNT_AVATAR_SIZE = 72
const EDIT_BUBBLE_SIZE = 28

export type AccountIdentityCardProps = {
    identity: UIStudentAccountIdentity
    xp?: number | null
    petals?: number | null
    /** Ouvre le menu de l'avatar. Absent : l'avatar ne presse pas. */
    onEditAvatar?: () => void
    /** L'avatar change (envoi, génération) : il s'estompe et ne répond plus. */
    avatarBusy?: boolean
    /** Son titre et le chemin vers le suivant, sous un filet. Absent (défaut) : rien. */
    titleProgress?: Pick<TitleProgressProps, "title" | "nextTitle" | "progress"> | null
    /** Ouvre le choix du titre : la ligne du titre presse. Sans effet sans `titleProgress`. */
    onChooseTitle?: () => void
    testID?: string
}

export function AccountIdentityCard(props: AccountIdentityCardProps) {
    const { identity, xp, petals, onEditAvatar, avatarBusy = false, titleProgress, onChooseTitle, testID } = props
    const { displayName, avatarUrl, className, subjects, memberSince, timezone } = identity

    const titleRow = titleProgress != null && (
        <Row gap="3" justify="between">
            <TitleProgress {...titleProgress} />
            {onChooseTitle != null && (
                <View style={styles.chevron}>
                    <Icon name="chevron-right" size={16} color="text-muted" />
                </View>
            )}
        </Row>
    )

    const avatar = (
        <Avatar uri={avatarUrl ?? undefined} name={displayName} size={ACCOUNT_AVATAR_SIZE} dimmed={avatarBusy} />
    )

    return (
        <Card p="4" testID={testID}>
            <Row gap="4" align="center">
                {onEditAvatar != null ? (
                    <Pressable
                        onPress={onEditAvatar}
                        disabled={avatarBusy}
                        accessibilityRole="button"
                        accessibilityLabel="Modifier mon avatar"
                        accessibilityState={{ disabled: avatarBusy, busy: avatarBusy }}
                        style={({ pressed }) => (pressed ? styles.pressed : undefined)}
                    >
                        {avatar}
                        <View style={styles.edit}>
                            <IconBubble iconName="pencil-alt" size={EDIT_BUBBLE_SIZE} color="role" iconColor="white" />
                        </View>
                    </Pressable>
                ) : (
                    avatar
                )}

                <Stack gap="1" style={styles.body}>
                    <Title>{displayName}</Title>
                    {className != null && className !== "" && <Text weight="semibold">{className}</Text>}
                    <SubjectBadges subjects={subjects} />
                    <Text size="text-sm" color="text-muted">
                        {`Membre depuis ${memberSince}`}
                    </Text>
                    {timezone != null && timezone !== "" && (
                        <Text size="text-sm" color="text-muted">
                            {`Fuseau horaire actuel : ${timezone}`}
                        </Text>
                    )}
                    <StudentCounters petals={petals ?? undefined} xp={xp ?? undefined} />
                </Stack>
            </Row>

            {titleProgress != null && (
                <>
                    <Divider spacing="3" />
                    {onChooseTitle != null ? (
                        <Pressable
                            onPress={onChooseTitle}
                            accessibilityRole="button"
                            accessibilityLabel={`Titre : ${titleProgress.title}`}
                            style={({ pressed }) => (pressed ? styles.pressed : undefined)}
                        >
                            {titleRow}
                        </Pressable>
                    ) : (
                        titleRow
                    )}
                </>
            )}
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    body: { flexShrink: 1, flexGrow: 1 },
    chevron: { flexShrink: 0 },
    pressed: { opacity: 0.6 },
    /** Le crayon mord sur le bord bas-droit de l'avatar. */
    edit: { position: "absolute", right: -EDIT_BUBBLE_SIZE / 7, bottom: -EDIT_BUBBLE_SIZE / 7 },
}))
